<script setup lang="ts">
/**
 * A heading in the document.
 *
 * The level is rendered as the matching element rather than as a class on a
 * `div`, so the document's outline is real: a screen reader, an export and a
 * contents page all read the same structure.
 *
 * Its number, where the document is numbered, comes from the store rather than
 * from anything here. A heading's number depends entirely on what came before
 * it, which is not something a block can know about itself.
 */
import { computed } from 'vue'

import { textStyleClassName } from '@/domain/layout/textStyles'
import type { HeadingSection } from '@/domain/section'
import { useReportStore } from '@/stores/report'

const props = defineProps<{ section: HeadingSection }>()

const report = useReportStore()

const number = computed(() => report.headingNumbers.get(props.section.id) ?? '')

/** The named style it is set in, if any: the document's heading style otherwise. */
const styleClass = computed(() =>
  props.section.textStyleId ? textStyleClassName(props.section.textStyleId) : '',
)
</script>

<template>
  <component :is="`h${section.level}`" :id="section.id" :class="styleClass">
    <span v-if="number" class="page-heading-number">{{ number }}</span>
    {{ section.title }}
  </component>
</template>
